<template>
  <div class="insert-component-section">
    <n-alert type="info" :bordered="false" class="insert-info">
      请选择要插入的零件或装配体，可放置在不同区域。
    </n-alert>

    <div class="insert-filter">
      <n-button-group size="small">
        <n-button
          :type="currentFilterMode === 'all' ? 'primary' : 'default'"
          @click="setFilterMode('all')"
        >
          全部
        </n-button>
        <n-button
          :type="currentFilterMode === 'cad_part' ? 'primary' : 'default'"
          @click="setFilterMode('cad_part')"
        >
          零件
        </n-button>
        <n-button
          :type="currentFilterMode === 'cad_assembly' ? 'primary' : 'default'"
          @click="setFilterMode('cad_assembly')"
        >
          装配体
        </n-button>
      </n-button-group>
    </div>

    <div class="insert-search">
      <n-input
        v-model:value="searchInput"
        size="small"
        placeholder="搜索名称..."
        clearable
      />
    </div>

    <div class="insert-list-header">
      <n-text depth="3" style="font-size: 12px;">
        要插入的零部件
      </n-text>
    </div>

    <div
      ref="listContainerRef"
      class="insert-list-container"
      @scroll="handleScroll"
    >
      <div
        v-for="file in fileList"
        :key="file.fileId"
        class="insert-list-item"
        :class="{ 'is-selected': isFileSelected(file) }"
        @click="toggleFileSelection(file)"
      >
        <n-checkbox
          size="small"
          :checked="isFileSelected(file)"
          @click.stop
          @update:checked="toggleFileSelection(file)"
        />
        <span class="insert-list-item-icon">
          <FeatureTreeNodeIcon
            :icon-type="file.kind === 'cad_assembly' ? 'assembly' : 'part'"
            :size="14"
          />
        </span>
        <span class="insert-list-item-label">{{ extractFileName(file.fileId) }}</span>
      </div>

      <div v-if="context.insertComponentLoading" class="insert-list-loading">
        <n-spin size="small" />
      </div>

      <div v-if="!context.insertComponentLoading && fileList.length === 0" class="insert-list-empty">
        <n-text depth="3" style="font-size: 12px;">暂无数据</n-text>
      </div>
    </div>

    <n-collapse :default-expanded-names="defaultExpandedNames" class="pm-collapse">
      <PuppyCollapseItem title="选项" name="options">
        <n-radio-group
          class="insert-options-radio-group"
          :value="currentFixationMode"
          @update:value="handleFixationModeChange"
        >
          <n-radio
            v-for="option in fixationOptions"
            :key="option.value"
            :value="option.value"
          >
            {{ option.label }}
          </n-radio>
        </n-radio-group>
      </PuppyCollapseItem>
    </n-collapse>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, watch, onUnmounted } from 'vue'
import {
  DEFAULT_INSERT_COMPONENT_FIXATION_MODE,
  InsertComponentFixationMode,
} from '@puppycad/cad-core'
import type { FileInfo } from '@puppycad/proto'
import type { PropertyManagerContext } from '@/core/property-manager/types'
import { extractFileName } from '@/utils/file-name'
import PuppyCollapseItem from '@/components/PuppyCollapseItem/PuppyCollapseItem.vue'
import FeatureTreeNodeIcon from '@/components/editor/FeatureTreeNodeIcon.vue'

type InsertFilterMode = 'all' | 'cad_part' | 'cad_assembly'
const ALL_INSERT_KINDS: string[] = ['cad_part', 'cad_assembly']
const defaultExpandedNames = ['options']

/**
 * SolidWorks 插入零部件 PropertyManager 的固定/浮动选项。
 *
 * 说明：
 * 1. 顺序与 SolidWorks 保持一致；
 * 2. 标签中的快捷键字符沿用用户截图中的中文版本；
 * 3. value 直接使用 cad-core 枚举，避免 UI 与提交链路出现字符串分叉。
 */
const fixationOptions: Array<{
  /** 选项显示文本。 */
  label: string
  /** 插入时固定/浮动策略枚举值。 */
  value: InsertComponentFixationMode
}> = [
  {
    label: '仅固定第一个零部件(N)',
    value: InsertComponentFixationMode.OnlyFirstComponent,
  },
  {
    label: '固定(F)',
    value: InsertComponentFixationMode.Fixed,
  },
  {
    label: '浮动(L)',
    value: InsertComponentFixationMode.Floating,
  },
]

const props = defineProps<{
  context: PropertyManagerContext
}>()

const listContainerRef = ref<HTMLElement | null>(null)

const searchInput = ref(props.context.insertComponentQuery ?? '')
let debounceTimer: ReturnType<typeof setTimeout> | null = null

watch(searchInput, (val) => {
  if (debounceTimer) clearTimeout(debounceTimer)
  debounceTimer = setTimeout(() => {
    props.context.setInsertComponentQuery?.(val)
  }, 300)
})

onUnmounted(() => {
  if (debounceTimer) clearTimeout(debounceTimer)
})

const fileList = computed(() => props.context.insertComponentFiles ?? [])

const selectedFileIdSet = computed(() => new Set(
  (props.context.selectedInsertComponentFiles ?? []).map((file) => file.fileId),
))

const currentFixationMode = computed(() => (
  props.context.insertComponentFixationMode ?? DEFAULT_INSERT_COMPONENT_FIXATION_MODE
))

const currentFilterMode = computed<InsertFilterMode>(() => {
  const kinds = props.context.insertComponentFilterKinds ?? []
  if (kinds.length === 1 && kinds[0] === 'cad_part') return 'cad_part'
  if (kinds.length === 1 && kinds[0] === 'cad_assembly') return 'cad_assembly'
  return 'all'
})

function setFilterMode(mode: InsertFilterMode): void {
  const kinds = mode === 'all' ? [...ALL_INSERT_KINDS] : [mode]
  props.context.setInsertComponentFilterKinds?.(kinds)
}

/**
 * 判断文件是否已经加入顺序插入队列。
 *
 * @param file 当前候选文件。
 * @returns 文件已勾选时返回 true。
 */
function isFileSelected(file: FileInfo): boolean {
  return selectedFileIdSet.value.has(file.fileId)
}

/**
 * 切换候选文件的插入勾选状态。
 *
 * @param file 当前候选文件。
 */
function toggleFileSelection(file: FileInfo): void {
  props.context.toggleInsertComponentSelection?.(file)
}

/**
 * 校验 NaiveUI radio 回传值是否为插入固定/浮动策略。
 *
 * @param value radio 回传值。
 * @returns 是否为合法策略枚举。
 */
function isInsertComponentFixationMode(value: unknown): value is InsertComponentFixationMode {
  return fixationOptions.some((option) => option.value === value)
}

/**
 * 同步插入时固定/浮动策略。
 *
 * @param mode radio 回传值。
 */
function handleFixationModeChange(mode: string | number | boolean): void {
  if (!isInsertComponentFixationMode(mode)) {
    return
  }
  props.context.setInsertComponentFixationMode?.(mode)
}

function handleScroll(): void {
  const el = listContainerRef.value
  if (!el) return
  const threshold = 50
  const atBottom = el.scrollHeight - el.scrollTop - el.clientHeight < threshold
  if (atBottom && props.context.insertComponentHasMore && !props.context.insertComponentLoading) {
    props.context.loadMoreInsertComponents?.()
  }
}
</script>

<style scoped lang="less">
.insert-component-section {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 4px 0;
}

.insert-info {
  font-size: 12px;
}

.insert-filter {
  display: flex;
  align-items: center;
  gap: 8px;
}

.insert-search {
  padding: 0;
}

.insert-list-header {
  padding: 4px 0 0;
}

.insert-list-container {
  height: 260px;
  min-height: 260px;
  overflow-y: auto;
  background: #ffffff;
  border: 1px solid #d8e1ec;
  border-radius: 4px;
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.85);
}

.insert-list-item {
  display: flex;
  align-items: center;
  gap: 8px;
  min-height: 30px;
  padding: 5px 8px;
  cursor: pointer;
  font-size: 12px;
  line-height: 1.4;
  color: #263445;
  background: #ffffff;
  transition: background 0.15s ease, color 0.15s ease;

  &:hover {
    background: #eef6ff;
    color: #172033;
  }

  &.is-selected {
    background: #dbeafe;
    color: #0f172a;
  }

  & + & {
    border-top: 1px solid #e3eaf2;
  }
}

.insert-list-item-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 14px;
  height: 14px;
  flex-shrink: 0;
  color: #667085;
}

.insert-list-item-label {
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.insert-list-loading {
  display: flex;
  justify-content: center;
  padding: 12px;
  border-top: 1px solid #e3eaf2;
}

.insert-list-empty {
  display: flex;
  justify-content: center;
  padding: 16px;
  color: #6b7280;
}

.pm-collapse :deep(.n-collapse-item__header) {
  padding: 6px 0;
  font-size: 12px;
}

.pm-collapse :deep(.n-collapse-item__content-inner) {
  padding-top: 4px;
}

.insert-options-radio-group {
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: 2px 0 4px;
  font-size: 12px;
}
</style>
